/**
 * Pathway: Infected insect bites and stings
 * Population: Adults and children aged 1 year and over
 * PGDs: 4a (flucloxacillin), 4b (clarithromycin), 4c (erythromycin)
 */

import type { Pathway } from "../types";

import { links } from "../../linksRegistry";


export const insectBitesPathway: Pathway = {
  id: "insectbites",
  name: "Infected insect bites and stings",
  population:
    "Adults and children aged 1 year and over (excluded: pregnant individuals under 16)",
  pgdRefs: [
    "pgd-4a-flucloxacillin-insectbites",
    "pgd-4b-clarithromycin-insectbites",
    "pgd-4c-erythromycin-insectbites",
  ],

  links,

  clinicalNotes: [
    "Do not offer an antibiotic if there are no signs or symptoms of infection.",
    "Be aware that a rapid-onset skin reaction to insect bite is likely to be an inflammatory or allergic reaction rather than an infection.",
    "Most insect bites and stings are not serious and will get better within a few hours or days, and do not need treatment with antibiotics.",
    "Consider likelihood of Lyme disease (tick bites are painless and can go unnoticed). Erythema migrans rash may be indistinguishable from other insect bites.",
  ],
  clinicalNoteLinkRefs: ["nice-cks-insect-bites", "nhs-england-pharmacy-first-clinical-pathways", "pgd-insect-bites-flucloxacillin", "pgd-insect-bites-clarithromycin", "pgd-insect-bites-erythromycin"],

  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "ageYears", label: "Age (years)", type: "number" },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Penicillin allergy is asked again in the flow",
      },
    ],
  },

  exclusionPrefillMap: {},

  entryNode: "q1_emergency_triage",

  nodes: [
    {
      id: "q1_emergency_triage",
      type: "checklist",
      text: "Does the patient have any of the following emergency presentations?",
      hint: "Tick any that apply.",
      showSepsisWarning: true,
      items: [
        {
          id: "anaphylaxis",
          text: "Signs of systemic hypersensitivity reaction or anaphylaxis — administer adrenaline",
        },
        {
          id: "immuno-with-infection",
          text: "Severely immunosuppressed AND has signs or symptoms of infection",
        },
        {
          id: "stung-mouth-throat",
          text: "Stung on the mouth, throat or tongue — risk of airway obstruction",
        },
        {
          id: "stung-eyes",
          text: "Stung around the eyes — risk of orbital cellulitis or compromised vision",
        },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q1a_pregnant_check",
      clinicalLinks: [
        {
          label: "NICE CKS — Insect Bites and Stings",
          url: links["nice-cks-insect-bites"].url,
          context: "reference",
        },
      ],
    },

    {
      id: "q1a_pregnant_check",
      type: "yes_no",
      text: "Is the patient currently pregnant or suspected pregnant?",
      required: true,
      storesTo: "isPregnant",
      // Skip for known-male patients
      autoAnswer: { when: { patientIsFemale: false }, value: false },
      yes: "q1b_pregnant_age_gate",
      no: "q2_special_bite_types",
    },

    {
      id: "q1b_pregnant_age_gate",
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 16 }, then: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" } },
      ],
      default: "q2_special_bite_types",
    },

    {
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "EMERGENCY — A&E referral",
      body: "Consider calculating NEWS2. Signpost to A&E or call 999 if life-threatening.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
      leafletRefs: [],
    },

    {
      id: "q2_special_bite_types",
      type: "checklist",
      text: "Does the patient meet ANY of the following criteria?",
      hint: "Tick any that apply.",
      imageCarousel: {
        title: "View clinical examples — Lyme disease rash",
        images: [
          {
            url: links["insectbites-image-1"].url,
            caption: "The rash can be less obvious on black or brown skin, and may look more like a bruise.",
          },
          {
            url: links["insectbites-image-2"].url,
            caption: "The rash may look like a bullseye on a dartboard.",
          },
          {
            url: links["insectbites-image-3"].url,
            caption: "Sometimes the edges of the rash may feel slightly raised.",
          },
        ],
      },
      items: [
        { id: "animal", text: "Bite or scratch caused by animal(s)" },
        { id: "human", text: "Bite caused by human(s)" },
        {
          id: "tick-lyme",
          text: "Bite caused by tick in the UK with signs of Lyme disease such as erythema migrans (bullseye) rash",
        },
        {
          id: "travel",
          text: "Bite or sting that occurred while travelling outside the UK with concern of insect-borne diseases (e.g. malaria, tick-borne encephalitis)",
        },
        {
          id: "exotic",
          text: "Bite or sting caused by an unusual or exotic insect",
        },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_SPECIAL_BITE" },
      noneSelected: "q3_48_hours",
      clinicalLinks: [
        {
          label: "NICE — Lyme disease",
          url: links["nice-lyme-rash-images"].url,
          context: "reference",
        },
      ],
    },

    {
      id: "q3_48_hours",
      type: "yes_no",
      text: "Has it been at least 48 hours since the initial insect bite or sting?",
      required: true,
      yes: "q4_itch_principal",
      no: { outcomeId: "OUT_SELF_CARE_UNDER48" },
    },

    {
      id: "q4_itch_principal",
      type: "yes_no",
      text: "Is itch the principal symptom (in the absence of other signs or symptoms of infection)?",
      required: true,
      yes: { outcomeId: "OUT_SELF_CARE_ITCH" },
      no: "q5_infection_signs",
    },

    {
      id: "q5_infection_signs",
      type: "multi_select",
      text: "Does the patient have acute onset of ≥3 of the following symptoms of an infected insect bite?",
      hint: "Tick all that apply. Three or more is required to meet the threshold.",
      minRequired: 3,
      items: [
        {
          id: "sign_redness",
          text: "Redness of skin (erythema may be more difficult to distinguish on darker skin tones)",
        },
        { id: "sign_pain", text: "Pain or tenderness to the area" },
        { id: "sign_swelling", text: "Swelling of skin" },
        { id: "sign_hot", text: "Skin surrounding the bite feels hot to touch" },
      ],
      thresholdMet: "GATEWAY_INFECTION_LIKELY",
      thresholdNotMet: { outcomeId: "OUT_SELF_CARE_INFECTION_UNLIKELY" },
    },

    {
      id: "GATEWAY_INFECTION_LIKELY",
      type: "gateway_progression",
      heading: "Infected insect bite more likely",
      body: "≥3 infection signs present. Proceeding to assess for spreading or progression.",
      triggersPayment: true,
      next: "q6_spreading",
    },

    {
      id: "q6_spreading",
      type: "checklist",
      text: "Does the patient meet ANY of the following criteria?",
      hint: "Tick any that apply.",
      items: [
        {
          id: "spreading",
          text: "Redness and swelling of skin surrounding the bite is spreading",
        },
        {
          id: "pustular",
          text: "There is evidence of pustular discharge at the site of the bite/sting",
        },
      ],
      includeNoneOption: true,
      anySelected: "q7_complicating_factors",
      noneSelected: { outcomeId: "OUT_SELF_CARE_NO_SPREAD" },
    },

    {
      id: "q7_complicating_factors",
      type: "checklist",
      text: "Does the patient meet ANY of the following criteria?",
      hint: "Tick any that apply.",
      items: [
        { id: "systemically_unwell", text: "Patient systemically unwell" },
        {
          id: "comorbidity",
          text: "Known comorbidity which may complicate or delay resolution of infection (e.g. peripheral arterial disease, chronic venous insufficiency, lymphoedema, morbid obesity)",
        },
        {
          id: "severe_pain",
          text: "Severe pain out of proportion to the wound (may indicate toxin-producing bacteria)",
        },
        {
          id: "numbness",
          text: "Numbness or tingling of the affected area",
        },
        {
          id: "fluid_pus",
          text: "Patient has significant collection of fluid or pus at site of infection (for incision and drainage where appropriate)",
        },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_COMPLICATING" },
      noneSelected: "q8_age",
    },

    {
      id: "q8_age",
      type: "yes_no",
      text: "Is the patient aged 1 year or over?",
      hint: "Inferred from patient date of birth where available.",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 1 }, value: true },
      yes: "q10_immuno",
      no: { outcomeId: "OUT_CANNOT_UNDER1" },
    },

    {
      id: "q10_immuno",
      type: "yes_no",
      text: "Is the patient severely immunosuppressed?",
      required: true,
      yes: { outcomeId: "OUT_REFER_IMMUNO" },
      no: "q11_allergy",
    },

    {
      id: "q11_allergy",
      type: "yes_no",
      text: "Does the patient have a known penicillin allergy? (Including allergy to flucloxacillin or severe hypersensitivity to any beta-lactam)",
      required: true,
      storesTo: "hasPenicillinAllergy",
      yes: "drug_select",
      no: "drug_select",
    },

    {
      id: "drug_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        // No penicillin allergy → flucloxacillin (any age 1+, pregnancy 16+ enforced via outcome below)
        {
          when: { hasPenicillinAllergy: false, isPregnant: false },
          pgdId: "pgd-4a-flucloxacillin-insectbites",
        },
        {
          when: { hasPenicillinAllergy: false, isPregnant: true },
          pgdId: "pgd-4a-flucloxacillin-insectbites",
        },
        // Penicillin allergic, not pregnant → clarithromycin
        {
          when: { hasPenicillinAllergy: true, isPregnant: false },
          pgdId: "pgd-4b-clarithromycin-insectbites",
        },
        // Penicillin allergic, pregnant → erythromycin (under-16 already blocked upstream by condition_route)
        {
          when: { hasPenicillinAllergy: true, isPregnant: true },
          pgdId: "pgd-4c-erythromycin-insectbites",
        },
      ],
      next: "weight_capture",
    },

    {
      id: "weight_capture",
      type: "weight_capture",
      text: "Confirm patient weight (kg) for paediatric clarithromycin dosing",
      hint: "Only required for children under 12 receiving clarithromycin under PGD 4b. Auto-skipped for adults and other PGDs.",
      minKg: 0.5,
      maxKg: 250,
      next: "pgd_exclusions",
    },

    {
      id: "pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY" },
    },
  ],

  outcomes: {
    OUT_SUPPLY: {
      id: "OUT_SUPPLY",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply antibiotic under PGD",
      reason: "Eligibility criteria met and PGD pre-supply checks cleared.",
      safetyNetting: [
        "If symptoms worsen rapidly or significantly at any time, seek urgent medical advice",
        "If symptoms do not improve after completion of the 5-day course, refer to GP or other provider",
      ],
      leafletRefs: [],
      linkRefs: [
        "target-selfcare-leaflet",
        "nhs-insect-bites",
        "ukhsa-tick-awareness",
        "anaphylaxis-campaign",
      ],
    },
    OUT_SELF_CARE_UNDER48: {
      id: "OUT_SELF_CARE_UNDER48",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care — less than 48 hours since bite",
      reason:
        "Recommend self-care, oral antihistamine and/or topical steroids OTC, and safety netting advice.",
      safetyNetting: [
        "Skin redness and itching are common and may last for up to 10 days",
        "It is unlikely that the skin will become infected",
        "Avoiding scratching may reduce inflammation and the risk of infection",
        "Return to pharmacy if symptoms worsen at any time OR do not improve after 3 days of OTC treatment for pharmacist reassessment",
      ],
      leafletRefs: [],
      linkRefs: ["nhs-insect-bites"],
    },
    OUT_SELF_CARE_ITCH: {
      id: "OUT_SELF_CARE_ITCH",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care — itch is principal symptom",
      reason:
        "Recommend self-care, oral antihistamine and/or topical steroids OTC, and safety netting advice.",
      safetyNetting: [
        "Skin redness and itching are common and may last for up to 10 days",
        "It is unlikely that the skin will become infected",
        "Avoiding scratching may reduce inflammation and the risk of infection",
        "Return to pharmacy if symptoms worsen at any time OR do not improve after 3 days of OTC treatment",
      ],
      leafletRefs: [],
      linkRefs: ["nhs-insect-bites"],
    },
    OUT_SELF_CARE_INFECTION_UNLIKELY: {
      id: "OUT_SELF_CARE_INFECTION_UNLIKELY",
      type: "outcome",
      outcomeType: "self_care",
      title: "Infected insect bite less likely — self-care",
      reason:
        "Fewer than 3 infection signs present. Recommend self-care, OTC antihistamine and/or topical steroids.",
      safetyNetting: [
        "Clearly demarcate the area and ask patient to monitor",
        "Return to pharmacy if symptoms worsen at any time OR do not improve after 3 days of OTC treatment for pharmacist reassessment",
      ],
      leafletRefs: [],
      linkRefs: ["nhs-insect-bites"],
    },
    OUT_SELF_CARE_NO_SPREAD: {
      id: "OUT_SELF_CARE_NO_SPREAD",
      type: "outcome",
      outcomeType: "self_care",
      title: "Infection signs present but not spreading — self-care",
      reason:
        "No spreading redness/swelling and no pustular discharge. Recommend self-care with close monitoring.",
      safetyNetting: [
        "Recommend self-care, oral antihistamine and/or topical steroids OTC",
        "Clearly demarcate the area and ask patient to monitor",
        "Return to pharmacy if symptoms worsen at any time OR do not improve after 3 days of OTC treatment for pharmacist reassessment",
      ],
      leafletRefs: [],
      linkRefs: ["nhs-insect-bites"],
    },
    OUT_REFER_SPECIAL_BITE: {
      id: "OUT_REFER_SPECIAL_BITE",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer — special bite type",
      reason:
        "Animal/human bite, tick with Lyme signs, travel-related, unusual/exotic insect. Refer to GP or other provider.",
      safetyNetting: [
        "Refer to GP or appropriate provider for assessment",
        "Return urgently if symptoms worsen",
      ],
      leafletRefs: [],
      linkRefs: ["nice-lyme-rash-images"],
    },
    OUT_REFER_COMPLICATING: {
      id: "OUT_REFER_COMPLICATING",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer — complicating factors",
      reason:
        "Systemically unwell, complicating comorbidity, severe pain, or significant fluid/pus collection. Refer to GP or other provider.",
      safetyNetting: [
        "Refer to GP or appropriate provider same day",
        "Call 999 if patient deteriorates rapidly",
      ],
      leafletRefs: [],
    },
    OUT_REFER_IMMUNO: {
      id: "OUT_REFER_IMMUNO",
      type: "outcome",
      outcomeType: "refer_urgent",
      title: "Refer urgently — severely immunosuppressed with infection signs",
      reason: "Refer to A&E for further assessment.",
      safetyNetting: [
        "Refer to A&E same day",
        "Call 999 if signs of sepsis or rapid deterioration",
      ],
      leafletRefs: [],
    },
    OUT_CANNOT_SUPPLY: {
      id: "OUT_CANNOT_SUPPLY",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — PGD exclusion met",
      reason:
        "One or more PGD pre-supply exclusions were triggered. Refer to prescriber if antibiotic appropriate but falls outside this PGD.",
      safetyNetting: [
        "Document the exclusion(s) that prevented supply",
        "Return urgently if symptoms worsen",
      ],
      leafletRefs: [],
      linkRefs: ["target-selfcare-leaflet", "nhs-insect-bites"],
    },
    OUT_CANNOT_UNDER1: {
      id: "OUT_CANNOT_UNDER1",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — under 1 year of age",
      reason: "Refer to GP or other provider.",
      safetyNetting: ["Advise self-care and safety netting in the interim"],
      leafletRefs: [],
    },
    OUT_CANNOT_PREGNANT_UNDER16: {
      id: "OUT_CANNOT_PREGNANT_UNDER16",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — pregnant and under 16",
      reason: "Refer to GP or other provider.",
      safetyNetting: ["Refer same day", "Return urgently if symptoms worsen"],
      leafletRefs: [],
    },
    OUT_CANNOT_NO_PGD: {
      id: "OUT_CANNOT_NO_PGD",
      type: "outcome",
      outcomeType: "cannot_supply",
      title:
        "No PGD available — pregnant, under 16, penicillin allergic",
      reason: "No PGD covers this combination. Refer to GP.",
      safetyNetting: ["Refer same day", "Return urgently if symptoms worsen"],
      leafletRefs: [],
    },
  },
};
